/**
 * Tax Utility Functions for India Payroll
 */

/**
 * Calculates Deductions for Old Tax Regime
 * @param {Object} employeeTaxData - The tax data for the employee
 * @param {Object} salaryTemplate - The salary template for the employee
 * @param {Object} employeeData - The employee data (should contain ctc)
 * @returns {Number} Total deductions amount
 */
export const calculateOldRegimeDeductions = (employeeTaxData, salaryTemplate, employeeData) => {
  if (!employeeTaxData || !employeeTaxData.taxDeclarations) return 0

  const declarations = employeeTaxData.taxDeclarations

  let total80C = 0
  let totalNPS = 0
  let homeLoanInterest = 0
  let rentPaid = 0
  let isMetro = false
  let total80G = 0
  let hraExemption = 0

  // 80D Buckets
  let d_Self = 0
  let d_Parents = 0
  let d_Checkup = 0
  let d_Self_Limit = 25000
  let d_Parents_Limit = 25000

  // Other specific limits
  let total80TTA = 0
  let total80TTB = 0
  let otherDeductionsSum = 0 // 80E, 80GG, etc.

  // Helper to sum investments in a declaration
  const sumInvestments = (decl) => {
    if (!decl.investments || !Array.isArray(decl.investments)) return 0
    return decl.investments.reduce((sum, inv) => {
      return sum + (parseFloat(inv.amount) || 0)
    }, 0)
  }

  declarations.forEach((decl) => {
    const amount = sumInvestments(decl)

    if (decl.section === '80C') {
      total80C += amount
    } else if (decl.section === '80CCD_1B') {
      totalNPS += amount
    } else if (decl.section === '24B') {
      homeLoanInterest += amount
    } else if (decl.section === '80G') {
      // 100% deduction for National Defence Fund, etc. 50% for others usually.
      if (
        ['PM_CARES', 'NATIONAL_DEFENCE', 'PRIME_MINISTER_RELIEF_FUND'].includes(decl.deductionKey)
      ) {
        total80G += amount
      } else {
        total80G += amount * 0.5
      }
    } else if (
      decl.section === 'HRA' ||
      (decl.deductionKey === 'RENT_PAID' && decl.section === 'HRA')
    ) {
      rentPaid += amount
      // Check for metro status in one of the investments if available
      if (decl.investments) {
        const metroInv = decl.investments.find((inv) => inv.extraDetails?.cityType === 'METRO')
        if (metroInv) isMetro = true
      }
    } else if (decl.section === '80D') {
      // Handle 80D Sub-limits
      if (decl.deductionKey === 'HEALTH_INSURANCE') {
        d_Self += amount
        // Check if user marked themselves as senior in *any* investment entry
        const isSenior = decl.investments?.some((inv) => inv.extraDetails?.isSeniorCitizen)
        if (isSenior) d_Self_Limit = 50000
      } else if (decl.deductionKey === 'PARENT_HEALTH_INSURANCE') {
        d_Parents += amount
        const isSenior = decl.investments?.some((inv) => inv.extraDetails?.isSeniorCitizen)
        if (isSenior) d_Parents_Limit = 50000
      } else if (decl.deductionKey === 'PREVENTIVE_CHECKUP') {
        d_Checkup += amount
      } else {
        // Fallback for generic 80D
        d_Self += amount
      }
    } else if (decl.section === '80TTA') {
      total80TTA += amount
    } else if (decl.section === '80TTB') {
      total80TTB += amount
    } else {
      // 80E, 80GG, U, etc.
      otherDeductionsSum += amount
    }
  })

  // Calculate 80D Final Amount
  const checkupClaim = Math.min(d_Checkup, 5000)
  const selfTotal = Math.min(d_Self + checkupClaim, d_Self_Limit)
  const parentTotal = Math.min(d_Parents, d_Parents_Limit)
  const total80D = selfTotal + parentTotal

  // Calculate 80TTA/TTB Limits
  total80TTA = Math.min(total80TTA, 10000)
  total80TTB = Math.min(total80TTB, 50000)
  const savingsInterestDed = total80TTA + total80TTB

  // Apply Statutory Limits on major sections
  const final80C = Math.min(total80C, 150000)
  const finalNPS = Math.min(totalNPS, 50000)
  const final24B = Math.min(homeLoanInterest, 200000)

  // Calculate HRA Exemption
  if (rentPaid > 0 && salaryTemplate && employeeData.ctc) {
    const annualCTC = parseFloat(employeeData.ctc) || 0
    const basicPercent = parseFloat(salaryTemplate.basicSalaryPercent) || 0
    const hraPercent = parseFloat(salaryTemplate.hraPercent) || 0

    const annualBasic = (annualCTC * basicPercent) / 100
    const annualHRA = (annualBasic * hraPercent) / 100

    const condition1 = annualHRA // Actual HRA received
    const condition2 = Math.max(0, rentPaid - annualBasic * 0.1) // Rent paid - 10% of basic
    const condition3 = annualBasic * (isMetro ? 0.5 : 0.4) // 50%/40% of basic

    if (rentPaid > 0) {
      hraExemption = Math.min(condition1, condition2, condition3)
    }
  }

  return (
    final80C +
    finalNPS +
    total80D +
    final24B +
    total80G +
    hraExemption +
    savingsInterestDed +
    otherDeductionsSum
  )
}

/**
 * Calculate Income Tax under Old Regime
 * @param {Number} taxableIncome
 * @returns {Number} Annual Tax
 */
export const calculateIncomeTaxOldRegime = (taxableIncome) => {
  // Old Tax Regime Slabs (General Citizens < 60)

  // 0 - 2.5L: Nil
  if (taxableIncome <= 250000) return 0

  let tax = 0

  // 2.5L - 5L: 5%
  if (taxableIncome > 250000) {
    tax += Math.min(taxableIncome - 250000, 250000) * 0.05
  }

  // 5L - 10L: 20%
  if (taxableIncome > 500000) {
    tax += Math.min(taxableIncome - 500000, 500000) * 0.2
  }

  // > 10L: 30%
  if (taxableIncome > 1000000) {
    tax += (taxableIncome - 1000000) * 0.3
  }

  // Rebate u/s 87A: Max 12,500 if Total Income <= 5L
  if (taxableIncome <= 500000) {
    tax = 0
  }

  // Cess 4%
  return tax * 1.04
}

/**
 * Calculate Income Tax under New Regime (FY 2025-26)
 * @param {Number} taxableIncome
 * @returns {Number} Annual Tax
 */
export const calculateIncomeTaxNewRegime = (taxableIncome) => {
  // New Tax Regime Slabs (FY 2025-26)
  if (taxableIncome <= 400000) return 0

  let tax = 0

  // 4L - 8L: 5%
  if (taxableIncome > 400000) {
    tax += Math.min(taxableIncome - 400000, 400000) * 0.05
  }

  // 8L - 12L: 10%
  if (taxableIncome > 800000) {
    tax += Math.min(taxableIncome - 800000, 400000) * 0.1
  }

  // 12L - 16L: 15%
  if (taxableIncome > 1200000) {
    tax += Math.min(taxableIncome - 1200000, 400000) * 0.15
  }

  // 16L - 20L: 20%
  if (taxableIncome > 1600000) {
    tax += Math.min(taxableIncome - 1600000, 400000) * 0.2
  }

  // 20L - 24L: 25%
  if (taxableIncome > 2000000) {
    tax += Math.min(taxableIncome - 2000000, 400000) * 0.25
  }

  // >24L: 30%
  if (taxableIncome > 2400000) {
    tax += (taxableIncome - 2400000) * 0.3
  }

  // Rebate u/s 87A: Tax is nil if Taxable Income <= 12 Lakhs
  if (taxableIncome <= 1200000) {
    tax = 0
  } else {
    // Marginal Relief logic
    const excessIncome = taxableIncome - 1200000
    if (tax > excessIncome) {
      tax = excessIncome
    }
  }

  // Cess 4%
  return tax * 1.04
}

/**
 * Calculate Monthly TDS and Annual Tax based on Regime
 * @param {Object} employeeData - Contains ctc
 * @param {Object} salaryTemplate - Contains salary structure percentages/values
 * @param {Object} employeeTaxData - Contains taxRegime and taxDeclarations
 * @returns {Object} { annualTax: Number, monthlyTax: Number }
 */
export const calculateTaxDetails = (employeeData, salaryTemplate, employeeTaxData) => {
  const annualCTC = parseFloat(employeeData.ctc) || 0
  if (annualCTC <= 0) return { annualTax: 0, monthlyTax: 0 }

  const annualProfessionalTax = parseFloat(salaryTemplate.professionalTax) || 0
  const regime = employeeTaxData?.taxRegime || 'new'

  // Standard Deduction: ₹75k for New Regime (FY25-26), ₹50k for Old Regime
  let standardDeduction = 0
  if (salaryTemplate.standardDeduction) {
    standardDeduction = parseFloat(salaryTemplate.standardDeduction)
  } else {
    standardDeduction = regime === 'old' ? 50000 : 75000
  }

  let taxableIncome = 0
  let annualTax = 0

  if (regime === 'old') {
    // Old Regime: Subtract HRA Exemption, 80C, 80D, etc.
    const exemptionsAndDeductions = calculateOldRegimeDeductions(
      employeeTaxData,
      salaryTemplate,
      employeeData
    )
    taxableIncome = Math.max(
      0,
      annualCTC - standardDeduction - annualProfessionalTax - exemptionsAndDeductions
    )
    annualTax = calculateIncomeTaxOldRegime(taxableIncome)
  } else {
    // New Regime: Mostly no deductions except Standard Deduction
    taxableIncome = Math.max(0, annualCTC - standardDeduction - annualProfessionalTax)
    annualTax = calculateIncomeTaxNewRegime(taxableIncome)
  }

  return {
    annualTax: parseFloat(annualTax.toFixed(2)),
    monthlyTax: parseFloat((annualTax / 12).toFixed(2))
  }
}

/**
 * Calculate Provident Fund (Employee & Employer)
 * @param {Number} monthlyBasicSalary
 * @returns {Object} { employeePF: Number, employerPF: Number }
 */
export const calculateProvidentFund = (monthlyBasicSalary) => {
  // Standard 12% of Basic
  const pfAmount = (monthlyBasicSalary * 12) / 100
  return {
    employeePF: parseFloat(pfAmount.toFixed(2)),
    employerPF: parseFloat(pfAmount.toFixed(2)) // Assuming Employer match is same for simplicity in this context
  }
}
